import { API_BASE_URL as API_URL } from '../config/apiBase';

export const powerBiService = {
  getEmbedToken: async (reportId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/powerbi/embed-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(reportId ? { reportId } : {})
    });

    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    if (!response.ok) {
      const detail = typeof data?.details === 'string' ? data.details : '';
      throw new Error(detail || data?.error || data?.message || `API Error: ${response.status}`);
    }

    if (!data?.token) {
      throw new Error('Embed token was not returned');
    }

    return data;
  },

  getToken: async (tier = 'Beta', getAuthHeaders) => {
    try {
      // console.log(`🔍 Fetching PowerBI token for tier: ${tier} from ${API_URL}/powerbi/get-powerbi-token`);
      
      const response = await fetch(`${API_URL}/powerbi/get-powerbi-token?tier=${encodeURIComponent(tier)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });
      
      // console.log(`📡 Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      // console.log('✅ PowerBI token received:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Error getting PowerBI token:', error);
      
      // If it's a network error (server not running), provide fallback
      if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        throw new Error('Server is not running. Please start the backend server first on port 5000.');
      }
      
      // If it's a JSON parsing error (HTML returned), server might be running but endpoint wrong
      if (error.message.includes('Unexpected token') || error.message.includes('<!DOCTYPE')) {
        throw new Error('Server is running but PowerBI endpoint is not available. Check server configuration.');
      }
      
      throw error;
    }
  },

  askAnalyticsAgent: async (question, history = []) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/powerbi/analytics-agent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ question, history }),
    });

    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      data = null;
    }

    if (!response.ok || data?.success === false) {
      const error = new Error(data?.message || 'Could not answer that question.');
      error.status = response.status;
      throw error;
    }

    return data;
  },

  refreshDataset: async () => {
    try {
      // console.log('🔄 Requesting dataset refresh...');
      
      const response = await fetch(`${API_URL}/powerbi/api/refresh-powerbi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Refresh API Error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      // console.log('✅ Dataset refresh initiated:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Error refreshing dataset:', error);
      throw error;
    }
  },

  getRefreshStatus: async (refreshId) => {
    try {
      // console.log(`📋 Checking refresh status for ID: ${refreshId}`);
      
      const response = await fetch(`${API_URL}/powerbi/api/refresh-status/${refreshId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Status API Error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      // console.log('✅ Refresh status received:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Error getting refresh status:', error);
      throw error;
    }
  },

  getRefreshQueue: async () => {
    try {
      // console.log('📊 Getting refresh queue status...');
      
      const response = await fetch(`${API_URL}/powerbi/api/refresh-queue`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Queue API Error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      // console.log('✅ Refresh queue status received:', data);
      return data;
      
    } catch (error) {
      console.error('❌ Error getting refresh queue:', error);
      throw error;
    }
  }
};

export const authService = {
  login: async (credentials) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('❌ Registration error:', error);
      throw error;
    }
  },

  me: async (token) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get user info');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      console.error('❌ Get user info error:', error);
      throw error;
    }
  }
};

export const sectorsService = {
  list: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/sectors`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      throw new Error('Failed to load sectors');
    }
    return res.json();
  },
  setUserSector: async (sectorId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/sectors/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ sectorId })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to set sector');
    }
    return res.json();
  }
};

export const reportsService = {
  list: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/reports`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load reports');
    }
    const data = await res.json();
    return data.data || [];
  },
  create: async ({ tier, report_type, report_uuid }) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ tier, report_type, report_uuid })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create report');
    }
    const data = await res.json();
    return data.data;
  },
  update: async (id, payload) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/reports/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update report');
    }
    const data = await res.json();
    return data.data;
  },
  remove: async (id) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/reports/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete report');
    }
    return true;
  }
};

export const adminService = {
  listUsers: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load users');
    }
    const data = await res.json();
    return data.data || [];
  },
  getUserDetails: async (userId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load user details');
    }
    const data = await res.json();
    return data.data;
  },
  updateUserRole: async (userId, role) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/role`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ role })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update user role');
    }
    const data = await res.json();
    return data.data;
  },
  updateUserStatus: async (userId, isActive) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ is_active: !!isActive })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update user status');
    }
    const data = await res.json();
    return data.data;
  },
  updateUserTier: async (userId, productTier) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/tier`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ product_tier: productTier })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update user tier');
    }
    const data = await res.json();
    return data.data;
  },
  addUserReport: async (userId, { tier, report_type, report_uuid }) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ tier, report_type, report_uuid })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to add report');
    }
    const data = await res.json();
    return data.data;
  },
  deleteUserReport: async (userId, reportUuid) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/users/${encodeURIComponent(userId)}/reports/${encodeURIComponent(reportUuid)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete report');
    }
    return true;
  },
  // Admin sectors: list and add reports for a sector
  getSectorReports: async (sectorId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/sectors/${encodeURIComponent(sectorId)}/reports`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load sector reports');
    }
    const data = await res.json();
    return data.data || { Beta: [], Alpha: [], 'Alpha+': [] };
  },
  addSectorReport: async (sectorId, { tier, report_type, report_uuid, apply_to_all }) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/admin/sectors/${encodeURIComponent(sectorId)}/reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ tier, report_type, report_uuid, apply_to_all: !!apply_to_all })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to add sector report');
    }
    const data = await res.json();
    return data.data;
  },
  deleteSectorReport: async (sectorId, reportUuid, removeFromAll = false) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/admin/sectors/${encodeURIComponent(sectorId)}/reports/${encodeURIComponent(reportUuid)}?remove_from_all=${removeFromAll ? 'true' : 'false'}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete sector report');
    }
    return true;
  }
};

export const schemaColumnsService = {
  listSchemas: async ({ includeDeleted = false } = {}) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/schema-columns/schemas?include_deleted=${includeDeleted ? 'true' : 'false'}`;
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load schemas');
    }
    const data = await res.json();
    return data.data || [];
  },

  list: async (schemaId) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/schema-columns?schema_id=${encodeURIComponent(schemaId)}`;
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load schema columns');
    }
    const data = await res.json();
    return data.data || [];
  },

  listAll: async ({ includeDeleted = false } = {}) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/schema-columns/all?include_deleted=${includeDeleted ? 'true' : 'false'}`;
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load schema columns');
    }
    const data = await res.json();
    return data.data || [];
  },

  // sync: async (schemaId) => {
  //   const token = localStorage.getItem('token');
  //   const url = `${API_URL}/schema-columns/sync?schema_id=${encodeURIComponent(schemaId)}`;
  //   const res = await fetch(url, {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //       ...(token ? { Authorization: `Bearer ${token}` } : {})
  //     }
  //   });
  //   if (!res.ok) {
  //     const err = await res.json().catch(() => ({}));
  //     throw new Error(err.message || 'Failed to sync schema columns');
  //   }
  //   return res.json();
  // },

  updateNormalized: async (schemaColId, normalized) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/schema-columns/${encodeURIComponent(schemaColId)}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ normalized_column_name: normalized })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update schema column');
    }
    const data = await res.json();
    return data.data;
  },

  exportXlsx: async (schemaId) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/schema-columns/export?schema_id=${encodeURIComponent(schemaId)}&format=xlsx`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to export schema columns');
    }
    const disposition = res.headers.get('content-disposition');
    let filename = 'schema_columns.xlsx';
    if (disposition) {
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(disposition);
      if (match?.[1]) filename = match[1].replace(/['"]/g, '');
    }
    const blob = await res.blob();
    return { blob, filename };
  },

  importFile: async (file) => {
    const token = localStorage.getItem('token');
    const url = `${API_URL}/schema-columns/import`;
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to import schema columns');
    }
    return res.json();
  }
};

export const fileService = {
  uploadFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('excelFile', file);

      // Get token from localStorage
      const token = localStorage.getItem('token');
      const headers = {};
      
      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/server/upload-excel`, {
        method: 'POST',
        headers: headers,
        body: formData,
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  downloadRejected: async (filePath, rejectedRowsPerSheet) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/server/download-rejected`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ filePath, rejectedRowsPerSheet })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} - ${errorText}`);
      }

      const disposition = response.headers.get('content-disposition');
      let filename = 'rejected_rows.xlsx';
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Error downloading rejected rows file:', error);
      throw error;
    }
  },

  // Download Excel file by ID and type (original or rejected)
  downloadExcel: async (fileId, type = 'original') => {
    try {
      // console.log(`📥 Downloading ${type} file with ID: ${fileId}`);
      
      const response = await fetch(`${API_URL}/download/${fileId}/${type}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      const { downloadUrl, fileName } = data;
      
      if (!downloadUrl) {
        throw new Error('No download URL received from server');
      }
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || `${type}_file.xlsx`;
      link.target = '_blank'; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // console.log(`✅ Download initiated for ${type} file: ${fileName}`);
      return true;
    } catch (error) {
      console.error(`Error downloading ${type} file:`, error);
      throw error;
    }
  },

  // Get user activity logs
  getActivityLogs: async (schemaId = null) => {
    try {
      // console.log('📊 Fetching activity logs...');
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Build URL with optional schema_id query parameter
      let url = `${API_URL}/server/activity`;
      if (schemaId) {
        url += `?schema_id=${encodeURIComponent(schemaId)}`;
      }
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch activity logs: ${response.status} - ${errorText}`);
      }
      
      const activityLogs = await response.json();
      //console.log(`✅ Retrieved ${activityLogs.length} activity logs`);
      return activityLogs;
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      throw error;
    }
  }
  ,

  // Delete a single upload by ID
  deleteUpload: async (uploadId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch(`${API_URL}/server/uploads/${encodeURIComponent(uploadId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete upload ${uploadId}: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting upload:', error);
      throw error;
    }
  }
};

export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_URL}/powerbi/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    return response.ok;
  } catch (error) {
    console.error('❌ Health check failed:', error);
    return false;
  }
};

export const entityService = {
  get: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/visuals/entity`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to load organisation');
    }
    const data = await res.json();
    return data.data || null;
  },

  uploadLogo: async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('logo', file);

    let res;
    try {
      res = await fetch(`${API_URL}/visuals/entity/logo`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData
      });
    } catch (networkError) {
      throw new Error('Could not reach the server. Check your connection and try again.');
    }

    // A 404 here almost always means the server has not been updated with
    // the logo endpoint yet, which is worth saying rather than hiding
    // behind a generic failure.
    if (res.status === 404) {
      throw new Error(
        'The logo endpoint is not available on this server yet. It needs the server update that adds /visuals/entity/logo.'
      );
    }
    if (res.status === 413) {
      throw new Error('That file is too large for the server to accept. Try one under 2MB.');
    }

    const body = await res.text();
    let data = {};
    if (body) {
      try {
        data = JSON.parse(body);
      } catch (parseError) {
        throw new Error(
          `Server returned an unexpected response (HTTP ${res.status}). It may not support logo uploads yet.`
        );
      }
    }

    if (!res.ok || !data.success) {
      throw new Error(data.message || `Failed to upload logo (HTTP ${res.status})`);
    }
    return data.data;
  },

  removeLogo: async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/visuals/entity/logo`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Failed to remove logo');
    }
    return true;
  }
};

export const leadsService = {
  submit: async (payload) => {
    let res;
    try {
      res = await fetch(`${API_URL}/server/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (networkError) {
      throw new Error('Could not reach the server. Please try again in a moment.');
    }

    if (res.status === 404) {
      throw new Error(
        'Enquiries are not available on this server yet. Please email sales@analyticax.in in the meantime.'
      );
    }

    const text = await res.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        throw new Error('The server returned an unexpected response. Please try again.');
      }
    }

    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Could not submit your enquiry. Please try again.');
    }
    return data.data || null;
  }
};

// Reads one of the five analytics templates for the signed-in user. The
// server pages at 1000 rows, so pull pages until the total is covered —
// capped, because this aggregates in the browser rather than in SQL.
export const analyticsTableService = {
  MAX_ROWS: 8000,

  fetchAll: async (tableName, { signal } = {}) => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const pageSize = 1000;

    let page = 1;
    let rows = [];
    let columns = [];
    let total = 0;
    let truncated = false;

    for (;;) {
      const res = await fetch(
        `${API_URL}/visuals/analytics-tables/${encodeURIComponent(tableName)}?limit=${pageSize}&page=${page}`,
        { headers, signal }
      );

      if (res.status === 404) {
        throw new Error(`${tableName} is not available on this server yet.`);
      }

      const text = await res.text();
      let body = {};
      if (text) {
        try {
          body = JSON.parse(text);
        } catch (parseError) {
          throw new Error('The server returned an unexpected response.');
        }
      }
      if (!res.ok || !body.success) {
        throw new Error(body.message || `Could not load ${tableName}.`);
      }

      const data = body.data || {};
      if (page === 1) {
        columns = Array.isArray(data.columns) ? data.columns : [];
        total = Number(data.totalRecords) || 0;
      }

      // The endpoint nests the cells under `values`; flatten so callers can
      // read a column straight off the row.
      const pageRows = Array.isArray(data.rows) ? data.rows : [];
      rows = rows.concat(
        pageRows.map((row) =>
          row && typeof row === 'object' && row.values && typeof row.values === 'object'
            ? { id: row.id, ...row.values }
            : row
        )
      );

      if (rows.length >= total || pageRows.length < pageSize) break;
      if (rows.length >= analyticsTableService.MAX_ROWS) {
        truncated = true;
        break;
      }
      page += 1;
    }

    return { columns, rows, total, truncated };
  }
};

// Grouped totals from the server, so a 63,000-row template does not have to
// cross the wire to be charted.
analyticsTableService.aggregate = async (spec, { signal } = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/visuals/analytics-aggregate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(spec),
    signal
  });

  if (res.status === 404) {
    throw new Error(
      'Chart totals are not available on this server yet. The analytics-aggregate endpoint needs deploying.'
    );
  }

  const text = await res.text();
  let body = {};
  if (text) {
    try {
      body = JSON.parse(text);
    } catch (parseError) {
      throw new Error('The server returned an unexpected response.');
    }
  }
  if (!res.ok || !body.success) {
    throw new Error(body.message || 'Could not work out those figures.');
  }
  return body.data || { rows: [] };
};

/**
 * Reads a spread of pages rather than the first few, for templates too large
 * to fetch whole. The endpoint orders by ctid, which tracks insert order, so
 * the opening pages are one end of the history: the first 5,000 rows of
 * Admissions cover five weeks of a sixteen-month table. Evenly spaced pages
 * cost the same number of requests and span the whole thing.
 *
 * The result is a sample. Shapes and proportions survive it; totals do not.
 */
analyticsTableService.fetchSample = async (tableName, { targetRows = 6000, signal } = {}) => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const pageSize = 1000;

  const readPage = async (page) => {
    const res = await fetch(
      `${API_URL}/visuals/analytics-tables/${encodeURIComponent(tableName)}?limit=${pageSize}&page=${page}`,
      { headers, signal }
    );
    if (res.status === 404) throw new Error(`${tableName} is not available on this server yet.`);
    const text = await res.text();
    let body = {};
    if (text) {
      try {
        body = JSON.parse(text);
      } catch (parseError) {
        throw new Error('The server returned an unexpected response.');
      }
    }
    if (!res.ok || !body.success) throw new Error(body.message || `Could not load ${tableName}.`);
    const data = body.data || {};
    const flat = (Array.isArray(data.rows) ? data.rows : []).map((row) =>
      row && typeof row === 'object' && row.values && typeof row.values === 'object'
        ? { id: row.id, ...row.values }
        : row
    );
    return { rows: flat, total: Number(data.totalRecords) || 0 };
  };

  const first = await readPage(1);
  const total = first.total;
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const wanted = Math.min(Math.max(Math.ceil(targetRows / pageSize), 1), totalPages);

  if (wanted >= totalPages) {
    // Small enough to read whole after all.
    const rest = [];
    for (let page = 2; page <= totalPages; page += 1) rest.push(readPage(page));
    const pages = await Promise.all(rest);
    const rows = pages.reduce((all, p) => all.concat(p.rows), first.rows.slice());
    return { rows, total, sampled: false };
  }

  // Page 1, then evenly spaced through to the last page.
  const numbers = new Set([1]);
  for (let i = 1; i < wanted; i += 1) {
    numbers.add(1 + Math.round((i * (totalPages - 1)) / (wanted - 1)));
  }
  const others = [...numbers].filter((p) => p !== 1).sort((a, b) => a - b);
  const pages = await Promise.all(others.map(readPage));
  const rows = pages.reduce((all, p) => all.concat(p.rows), first.rows.slice());

  return { rows, total, sampled: true };
};
